import React, { useEffect, useState } from 'react';
import { notify } from 'react-notify-toast';
import {
  AlertToast,
  AlertToastViewport,
  ToastStackColumn,
  ToastStackSlot,
  TOAST_EXIT_MS,
  type AlertToastType,
} from '../components/AlertToast';
import eventBus from './globalEventBus';

const DEFAULT_ERROR_MESSAGE = 'An error occurred. Please try again.';

type ToastType = AlertToastType;

export type { AlertToastType as ToastType };

type AlertMessage = React.ReactNode;

type ToastState = {
  id: number;
  msg: AlertMessage;
  toastType: ToastType;
  duration: number;
  remaining: number;
  isPaused: boolean;
  progressKey: number;
  startedAt?: number;
  isExiting?: boolean;
};

type ToastListener = (stack: ToastState[]) => void;

const toastListeners = new Set<ToastListener>();
const hideTimers = new Map<number, ReturnType<typeof setTimeout>>();
const exitTimers = new Map<number, ReturnType<typeof setTimeout>>();
let nextToastId = 1;
let currentToastStack: ToastState[] = [];

function emitToasts(next: ToastState[]): void {
  currentToastStack = next;
  toastListeners.forEach(listener => listener(next));
}

function clearHideTimerFor(id: number): void {
  const t = hideTimers.get(id);
  if (t) {
    clearTimeout(t);
    hideTimers.delete(id);
  }
}

function clearExitTimerFor(id: number): void {
  const t = exitTimers.get(id);
  if (t) {
    clearTimeout(t);
    exitTimers.delete(id);
  }
}

function clearAllToastTimers(): void {
  hideTimers.forEach(timer => clearTimeout(timer));
  hideTimers.clear();
  exitTimers.forEach(timer => clearTimeout(timer));
  exitTimers.clear();
}

function scheduleToastHide(id: number, delay: number): void {
  clearHideTimerFor(id);
  if (delay === -1) return;

  hideTimers.set(
    id,
    setTimeout(() => {
      hideTimers.delete(id);
      beginToastExit(id);
    }, delay)
  );
}

function beginToastExit(id: number): void {
  const toast = currentToastStack.find(t => t.id === id);
  if (!toast || toast.isExiting) return;

  clearHideTimerFor(id);

  emitToasts(currentToastStack.map(t => (t.id === id ? { ...t, isExiting: true } : t)));

  clearExitTimerFor(id);
  exitTimers.set(
    id,
    setTimeout(() => {
      exitTimers.delete(id);
      emitToasts(currentToastStack.filter(t => t.id !== id));
    }, TOAST_EXIT_MS)
  );
}

function dismissToast(id: number): void {
  beginToastExit(id);
}

function pauseToast(id: number): void {
  const toast = currentToastStack.find(t => t.id === id);
  if (!toast || toast.isExiting || toast.duration === -1 || toast.isPaused) return;

  const elapsed = Date.now() - (toast.startedAt || Date.now());
  const remaining = Math.max(0, toast.remaining - elapsed);
  clearHideTimerFor(id);
  emitToasts(currentToastStack.map(t => (t.id === id ? { ...t, remaining, isPaused: true, startedAt: undefined } : t)));
}

function resumeToast(id: number): void {
  const toast = currentToastStack.find(t => t.id === id);
  if (!toast || toast.isExiting || toast.duration === -1 || !toast.isPaused) return;

  const nextRemaining = Math.max(0, toast.remaining);
  if (nextRemaining === 0) {
    beginToastExit(id);
    return;
  }

  const startedAt = Date.now();
  emitToasts(
    currentToastStack.map(t =>
      t.id === id ? { ...t, remaining: nextRemaining, isPaused: false, startedAt, progressKey: t.progressKey + 1 } : t
    )
  );
  scheduleToastHide(id, nextRemaining);
}

type AlertService = {
  show: (msg: AlertMessage, type?: ToastType, timeout?: number) => void;
  showOnNextPage: (msg: AlertMessage, type?: ToastType, timeout?: number) => void;
  showError: (msg?: AlertMessage, duration?: number) => void;
  hide: typeof notify.hide;
};

export function AlertToastHost() {
  const [toastStack, setToastStack] = useState<ToastState[]>([]);

  useEffect(() => {
    const listener = (stack: ToastState[]) => setToastStack(stack);
    toastListeners.add(listener);
    setToastStack(currentToastStack);
    return () => {
      toastListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    return eventBus.subscribe('VideoIsViewable', () => {
      alertService.show(
        <p>
          <strong>Encoding Complete!</strong>
          <br />
          Your video is encoded and ready to be viewed.
        </p>,
        'success'
      );
    });
  }, []);

  if (toastStack.length === 0) return null;

  const stackSize = toastStack.length;

  return (
    <AlertToastViewport>
      <ToastStackColumn>
        {toastStack.map((t, index) => (
          <ToastStackSlot key={t.id} $stackIndex={index} $stackSize={stackSize}>
            <AlertToast
              type={t.toastType ?? 'info'}
              message={t.msg}
              onClose={() => dismissToast(t.id)}
              isExiting={Boolean(t.isExiting)}
              showProgress={t.duration !== -1}
              progressDuration={t.duration}
              progressRemaining={t.remaining}
              progressPaused={t.isPaused}
              progressKey={t.progressKey}
              onMouseEnter={t.isExiting ? undefined : () => pauseToast(t.id)}
              onMouseLeave={t.isExiting ? undefined : () => resumeToast(t.id)}
            />
          </ToastStackSlot>
        ))}
      </ToastStackColumn>
    </AlertToastViewport>
  );
}

const alertService: AlertService = {
  show: (msg, type = 'success', timeout = 5000) => {
    const duration = type === 'error' || type === 'danger' ? 20000 : timeout;
    const toast: ToastState = {
      id: nextToastId++,
      msg,
      toastType: type,
      duration,
      remaining: duration,
      isPaused: false,
      progressKey: 0,
      startedAt: duration === -1 ? undefined : Date.now(),
    };
    emitToasts([toast, ...currentToastStack]);
    if (duration !== -1) {
      scheduleToastHide(toast.id, duration);
    }
  },
  showOnNextPage: (msg, type = 'success', timeout = 5000) => {
    const onHashChange = () => {
      alertService.show(msg, type, timeout);

      window.removeEventListener('hashchange', onHashChange);
    };
    window.addEventListener('hashchange', onHashChange);
  },
  showError: (msg = DEFAULT_ERROR_MESSAGE, duration = 20000) => alertService.show(msg, 'error', duration),
  hide: () => {
    clearAllToastTimers();
    emitToasts([]);
    notify.hide();
  },
};

export default alertService;
